import { useEffect, useRef } from 'react';
import { Button, Snackbar, Alert } from '@mui/material';
import { useRegisterSW } from 'virtual:pwa-register/react';

const POLL_INTERVAL_MS = 60 * 1000;

export default function PwaUpdatePrompt() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) registrationRef.current = registration;
    },
  });

  useEffect(() => {
    const tick = () => {
      const reg = registrationRef.current;
      if (!reg || reg.installing || !navigator.onLine) return;
      reg.update().catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };

    const interval = setInterval(tick, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', tick);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', tick);
    };
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister());
      });
    }
  }, []);

  const handleReload = () => {
    updateServiceWorker(true);
  };

  return (
    <Snackbar
      open={needRefresh}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity="info"
        variant="filled"
        action={
          <>
            <Button color="inherit" size="small" onClick={() => setNeedRefresh(false)}>
              Later
            </Button>
            <Button color="inherit" size="small" onClick={handleReload}>
              Reload
            </Button>
          </>
        }
      >
        A new version of Music 'n Me is available.
      </Alert>
    </Snackbar>
  );
}
